import { useState } from 'react'
import { Plus, Sparkles } from 'lucide-react'

import type { Section } from './types'
import { SECTION_TEMPLATES, TEMPLATE_CATEGORIES } from './sectionTemplates'

interface Props {
  onAdd: (tag: NonNullable<Section['tag']>) => void
  /**
   * Insert a fully-composed section from the template library. Optional so
   * hosts that only support blank sections keep compiling; when absent the
   * "Start from a template" tab is hidden rather than rendering a dead
   * affordance.
   */
  onAddTemplate?: (section: Section) => void
  position: 'top' | 'middle' | 'bottom'
  hasHeader: boolean
  hasFooter: boolean
  hasPopup: boolean
}

const ALL_TAGS: Array<{ tag: NonNullable<Section['tag']>; label: string; help: string }> = [
  { tag: 'header', label: 'Header',  help: 'Always at the top — logo, nav, banner.' },
  { tag: 'footer', label: 'Footer',  help: 'Always at the bottom — copyright, links.' },
  { tag: 'aside',  label: 'Aside',   help: 'Side region — promo, filters, callouts.' },
  { tag: 'popup',  label: 'Pop-up',  help: 'Floating buttons / links that open a modal (chat, settings).' },
  { tag: 'custom', label: 'Section', help: 'A free-form section.' },
]

export function AddSectionButton({ onAdd, onAddTemplate, position, hasHeader, hasFooter, hasPopup }: Props) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<'blank' | 'template'>('template')
  const allowed = ALL_TAGS.filter(t => {
    if (t.tag === 'header' && (hasHeader || position !== 'top')) return false
    if (t.tag === 'footer' && (hasFooter || position !== 'bottom')) return false
    if (t.tag === 'popup' && hasPopup) return false
    return true
  })
  if (allowed.length === 0) return null

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: '6px 0', position: 'relative',
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 12px', fontSize: 13, fontWeight: 500,
          border: '1px dashed var(--color-border)', borderRadius: 999,
          background: 'var(--color-bg)',
          color: 'var(--color-text-secondary)', cursor: 'pointer',
          opacity: 0.6, transition: 'opacity 0.12s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
      >
        <Plus size={12} /> Add a section
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
              marginTop: 6, zIndex: 11, minWidth: 320,
              background: 'var(--color-bg)',
              border: '1px solid var(--color-border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              padding: 6,
            }}
          >
            {/* Templates first, deliberately: a blank section is the thing
                an author falls back to, not the thing they should have to
                start from. Each template already uses the motion, hover,
                layout and icon layers, so it demonstrates the capability as
                well as saving the work. */}
            {onAddTemplate && (
              <div style={{ display: 'flex', gap: 4, padding: '2px 2px 6px', borderBottom: '1px solid var(--color-border)', marginBottom: 6 }}>
                {(['template', 'blank'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    style={{
                      flex: 1, padding: '6px 8px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: 'none', borderRadius: 6,
                      background: tab === t ? 'var(--color-bg-secondary)' : 'transparent',
                      color: tab === t ? 'var(--color-text)' : 'var(--color-text-secondary)',
                    }}
                  >
                    {t === 'template' ? 'From a template' : 'Blank'}
                  </button>
                ))}
              </div>
            )}

            {onAddTemplate && tab === 'template' ? (
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {TEMPLATE_CATEGORIES.map((cat) => {
                  const items = SECTION_TEMPLATES.filter((t) => t.category === cat)
                  if (!items.length) return null
                  return (
                    <div key={cat}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                        color: 'var(--color-text-secondary)', padding: '8px 10px 4px',
                      }}>{cat}</div>
                      {items.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => { onAddTemplate(tpl.build()); setOpen(false) }}
                          style={{
                            display: 'flex', gap: 8, alignItems: 'flex-start', width: '100%', textAlign: 'left',
                            padding: '8px 10px', border: 'none', background: 'transparent',
                            cursor: 'pointer', borderRadius: 4,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <Sparkles size={13} style={{ marginTop: 3, flexShrink: 0, color: 'var(--color-accent)' }} />
                          <span>
                            <span style={{ display: 'block', fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>{tpl.name}</span>
                            <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)', lineHeight: 1.4 }}>{tpl.description}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )
                })}
              </div>
            ) : (
              allowed.map(t => (
                <button
                  key={t.tag}
                  type="button"
                  onClick={() => { onAdd(t.tag); setOpen(false) }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '8px 10px', border: 'none', background: 'transparent',
                    cursor: 'pointer', borderRadius: 4,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)' }}>{t.label}</div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{t.help}</div>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
